import { Component, Input, Output, EventEmitter, forwardRef, Injector, AfterViewInit, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NG_VALIDATORS, Validator, AbstractControl, ValidationErrors, NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';

export interface SelectOption {
    value: string | number;
    label: string;
    translationKey?: string;
}

@Component({
    selector: 'app-default-select',
    templateUrl: './default-select.component.html',
    styleUrls: ['./default-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DefaultSelectComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => DefaultSelectComponent),
            multi: true
        }
    ]
})
export class DefaultSelectComponent implements ControlValueAccessor, Validator, AfterViewInit, OnDestroy {
    @Input() label: string = '';
    @Input() options: SelectOption[] = [];
    @Input() placeholder: string = '';
    @Input() placeholderTranslationKey: string = '';
    @Input() disabled: boolean = false;
    @Input() required: boolean = false;
    @Input() requiredErrorKey: string = '@T.COMMON.FORM-ERRORS.REQUIRED';
    @Input() invalidErrorKey: string = '@T.COMMON.FORM-ERRORS.INVALID';

    @Output() selectionChange = new EventEmitter<string | number>();

    selectedValue: string | number = '';
    private onChange = (value: string | number) => { };
    private onTouched = () => { };
    private onValidatorChange = () => { };
    private ngControl: NgControl | null = null;
    private statusSubscription: Subscription | null = null;

    constructor(private injector: Injector) { }

    ngAfterViewInit() {
        // Try to get the NgControl after view init to access validation state
        this.ngControl = this.injector.get(NgControl, null);

        // Subscribe to status changes to update error display
        if (this.ngControl && this.ngControl.control) {
            this.statusSubscription = this.ngControl.control.statusChanges.subscribe(() => {
                // Trigger change detection when status changes
            });
        }
    }

    ngOnDestroy() {
        if (this.statusSubscription) {
            this.statusSubscription.unsubscribe();
        }
    } get value(): string | number {
        return this.selectedValue;
    }

    set value(val: string | number) {
        this.selectedValue = val;
        this.onChange(val);
        this.onTouched();
    }

    // ControlValueAccessor implementation
    writeValue(value: string | number): void {
        this.selectedValue = value || '';
    }

    registerOnChange(fn: (value: string | number) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    onSelectionChange(): void {
        this.onChange(this.selectedValue);
        this.onTouched();
        this.selectionChange.emit(this.selectedValue);
        this.onValidatorChange();
    }

    get hasPlaceholder(): boolean {
        return !!(this.placeholder || this.placeholderTranslationKey);
    }

    get placeholderText(): string {
        if (this.placeholderTranslationKey) {
            return this.placeholderTranslationKey;
        }
        return this.placeholder;
    }

    // Validator implementation
    validate(control: AbstractControl): ValidationErrors | null {
        // This will be called by Angular's validation system
        return null; // Custom validation logic can be added here if needed
    }

    registerOnValidatorChange(fn: () => void): void {
        this.onValidatorChange = fn;
    }

    // Error handling methods
    get hasErrors(): boolean {
        return this.ngControl ? !!(this.ngControl.errors && this.ngControl.touched) : false;
    }

    get errorMessages(): string[] {
        if (!this.ngControl || !this.ngControl.errors || !this.ngControl.touched) {
            return [];
        }

        const errors = [];
        if (this.ngControl.errors['required']) {
            errors.push(this.requiredErrorKey);
        }
        if (this.ngControl.errors['invalid']) {
            errors.push(this.invalidErrorKey);
        }

        return errors;
    }
}
